import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AgentOrbType } from '../agent-orb/agent-orb.component';
import { OrbiController } from './vendor/core/controller.js';
import { createOrbi } from './vendor/core/orbi.js';
import { createPersonalityEffects } from './vendor/core/personality-effects.js';
import { sequenceRegistry } from './vendor/sequences/registry.js';

/** `--orbi-size` in vendor/core/orbi.css — die Größe, für die alle Pixelwerte
 *  der Lab-Sequenzen gebaut sind. */
const LAB_ORB_SIZE = 220;

/** Blickrichtung (Lab `setGaze`): `scan` = Pendel links/rechts im Wechsel,
 *  sonst bleibt er dort und schaut nur selten kurz zur Seite. */
export type OrbiGaze = 'scan' | 'left' | 'right' | 'center';

/** Platzhalter in `script`: Orbi schaut normal (Idle-Augen, keine Sequenz). */
export const ORBI_IDLE = 'idle';

/** Dauer eines `ORBI_IDLE`-Schritts; mehrere hintereinander addieren sich. */
const IDLE_MS = 2500;

/** Streuung beim Start, damit Orbis ohne einleitendes Idle nicht exakt
 *  gleichzeitig loslegen, wenn Dialog oder Dashboard aufgehen. */
const START_JITTER_MS = 600;

/** `hide-surprise` im Lab verdeckt den ins Loch gefallenen Orbi mit einer
 *  220px hohen Bodenfläche in Bühnenfarbe (core/hide-surprise-keyframes.js,
 *  `groundCover`: deckend von 16% bis 70.5%). Auf einer Karte läge die über
 *  dem Text darunter — hier wird der Orbi stattdessen im selben Fenster an der
 *  Bodenlinie abgeschnitten (`--orbi-ground-y` = 50% + 98px = 12px über dem
 *  unteren Bühnenrand). Umgeschaltet wird in der Mitte der Lab-Überblendungen. */
const HIDE_SURPRISE_CLIP: Keyframe[] = [
  { clipPath: 'inset(-400px)', offset: 0 },
  { clipPath: 'inset(-400px)', offset: 0.13 },
  { clipPath: 'inset(-400px -400px 12px -400px)', offset: 0.1301 },
  { clipPath: 'inset(-400px -400px 12px -400px)', offset: 0.72 },
  { clipPath: 'inset(-400px)', offset: 0.7201 },
  { clipPath: 'inset(-400px)', offset: 1 },
];

interface QueueStep {
  name: string;
  pauseAfter: number;
}

/** Wandelt ein Skript wie `['idle', 'giggle-bounce', 'idle']` in Schritte mit
 *  Pause danach. Idle zwischen zwei Sequenzen wird `pauseAfter` der vorigen;
 *  einleitende Idles laufen einmal vor dem ersten Durchgang und — weil das
 *  Skript loopt — danach jedes Mal zwischen Ende und Neustart. Die Pausen
 *  wartet die Komponente selbst ab statt über `queue({ pauseAfter })` des
 *  Lab-Controllers: der begrenzt Pausen auf 5000ms, das Dashboard braucht
 *  längere Ruhe zwischen den Ausdrücken. */
function toQueue(script: string[]): { leadMs: number; steps: QueueStep[] } {
  let lead = 0;
  while (script[lead] === ORBI_IDLE) lead += 1;
  const steps: QueueStep[] = [];
  for (const name of script.slice(lead)) {
    if (name === ORBI_IDLE) {
      if (steps.length) steps[steps.length - 1].pauseAfter += IDLE_MS;
      continue;
    }
    steps.push({ name, pauseAfter: 0 });
  }
  if (steps.length) steps[steps.length - 1].pauseAfter += lead * IDLE_MS;
  return { leadMs: lead * IDLE_MS, steps };
}

/** Nach dieser Ruhe gilt das Scrollen als beendet. */
const SCROLL_SETTLE_MS = 180;

/** Ein gemeinsamer Scroll-Listener für alle Orbis. `capture`, weil das Portal
 *  nicht im Fenster scrollt, sondern in `nb-layout .scrollable-container`
 *  (und Dialoge in ihrem eigenen Body) — scroll-Events blubbern nicht. */
const scrollSubscribers = new Set<(scrolling: boolean) => void>();
let scrolling = false;
let scrollSettleTimer: ReturnType<typeof setTimeout> | null = null;

function onAnyScroll(): void {
  if (!scrolling) {
    scrolling = true;
    scrollSubscribers.forEach((notify) => notify(true));
  }
  if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
  scrollSettleTimer = setTimeout(() => {
    scrolling = false;
    scrollSubscribers.forEach((notify) => notify(false));
  }, SCROLL_SETTLE_MS);
}

function subscribeScroll(notify: (scrolling: boolean) => void): () => void {
  if (!scrollSubscribers.size) {
    document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
  }
  scrollSubscribers.add(notify);
  return () => {
    scrollSubscribers.delete(notify);
    if (!scrollSubscribers.size) document.removeEventListener('scroll', onAnyScroll, { capture: true });
  };
}

/** Wartezeit nach dem Verlassen, bevor der Play-Zustand beendet wird — so
 *  lange läuft im Lab die Rückkehr-Animation der Augen (360ms). */
const PLAY_LEAVE_SETTLE_MS = 420;

type PlayStopState = 'idle' | 'play-hover' | 'stop' | 'stop-hover' | 'resetting';

let instanceCounter = 0;

/**
 * Orbi direkt aus dem orbi-animation-lab (tools/, vendor/ = 1:1-Kopie von
 * core/ und sequences/ aus scripts/sync_orbi_lab.py, nur die `?v=`-Import-
 * Anhänge entfernt).
 *
 * Die Bühne läuft in Lab-Größe (220px) und wird als Ganzes auf `size`
 * verkleinert — dadurch stimmen Sprunghöhen, Noten, Speed Lines und Konfetti
 * ohne jede Umrechnung. Ohne View-Encapsulation, weil das Lab sein DOM selbst
 * baut (`createOrbi`) und Angulars Attribut-Scoping dort nicht ankommt; alle
 * Lab-Selektoren sind `.orbi*` bzw. auf `.orbi-lab-stage` gescoped.
 */
@Component({
  selector: 'app-orbi-lab',
  templateUrl: './orbi-lab.component.html',
  styleUrls: ['./orbi-lab.component.scss', './vendor/core/orbi.css', './orbi-lab-stage.css'],
  encapsulation: ViewEncapsulation.None,
})
export class OrbiLabComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() type: AgentOrbType = 'tutor';

  /** Gleiche Bedeutung wie bei `app-agent-orb`: der Orb rendert 8px größer. */
  @Input() size = 96;

  /** Endlos wiederholter Ablauf aus Sequenz-IDs (vendor/sequences/registry.js)
   *  und `ORBI_IDLE` für "normal schauen". */
  @Input() script: string[] = [];

  /** Blickrichtung; `gazeJitter` (0–1) verschiebt, wann er kurz wegschaut.
   *  Ohne `gazeJitter` startet jeder Orbi an einer zufälligen Stelle — beim
   *  Pendel also mal links, mal rechts (Marcel, 05.11.2026: vorher schauten
   *  alle zu Beginn nach rechts). */
  @Input() gaze: OrbiGaze = 'scan';
  @Input() gazeJitter: number | null = null;

  /** Orbi wird zum Play-/Stop-Button (Lab-Sequenz `play-button`): Hover oder
   *  Fokus unterbricht den Ablauf und zeigt Play, Klick meldet `playRequest`,
   *  im Stop-Zustand meldet Klick `stopRequest`. Danach läuft `script` weiter. */
  @Input() playStop = false;

  /** Läuft das, was Play startet, gerade (z.B. der Vorschau-Chat)? Wird es
   *  von außen beendet oder gestartet, zieht der Orbi nach. */
  @Input() active = false;

  @Output() readonly playRequest = new EventEmitter<void>();
  @Output() readonly stopRequest = new EventEmitter<void>();

  @ViewChild('mount', { static: true }) private mountRef!: ElementRef<HTMLDivElement>;

  readonly uid = `orbi-lab-${++instanceCounter}`;

  private orbi: ReturnType<typeof createOrbi> | null = null;
  private controller: OrbiController | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private clipAnimation: Animation | null = null;
  private destroyed = false;

  /** Warum der Orbi gerade steht — außerhalb des Bildschirms und/oder weil
   *  gescrollt wird. Leer = er läuft. */
  private readonly pauseReasons = new Set<'offscreen' | 'scroll'>();
  /** Beim Pausieren angehaltene Sequenz-Animationen, zum Fortsetzen. */
  private pausedAnimations: Animation[] = [];
  /** Nächster Schritt, falls er während einer Pause fällig wurde. */
  private pendingStep: (() => void) | null = null;
  private visibilityObserver: IntersectionObserver | null = null;
  private unsubscribeScroll: (() => void) | null = null;

  /** Schritte aus `script` und ein Zähler, der beim Unterbrechen hochläuft —
   *  eine noch wartende Runde der alten Schleife bricht daran ab. */
  private steps: QueueStep[] = [];
  private loopGeneration = 0;
  private reducedMotion = false;
  /** `loop` = `script` läuft, `interactive` = Lab-Sequenz `play-button`. */
  private mode: 'loop' | 'interactive' = 'loop';
  private leaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  /** Auf der Agent-Kachel steht der Typ erst fest, wenn die Templates geladen
   *  sind — dann Farben und Idle-Takt über die Lab-API umstellen. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type'] && this.orbi) this.orbi.setType(this.type);
    if ((changes['gaze'] || changes['gazeJitter']) && this.orbi) this.orbi.setGaze(this.gaze, this.gazeJitter ?? Math.random());
    if (changes['active'] && this.orbi && this.playStop) {
      this.zone.runOutsideAngular(() => this.syncWithActive());
    }
  }

  @HostBinding('class') get hostClass(): string {
    return this.playStop ? 'orbi-lab orbi-lab--interactive' : 'orbi-lab';
  }
  @HostBinding('style.width.px') get hostWidth(): number { return this.boxSize; }
  @HostBinding('style.height.px') get hostHeight(): number { return this.boxSize; }

  get boxSize(): number {
    return this.size + 8;
  }

  get stageTransform(): string {
    return `translate(-50%, -50%) scale(${this.boxSize / LAB_ORB_SIZE})`;
  }

  get speedBackId(): string { return `${this.uid}-speed-back`; }
  get speedFrontId(): string { return `${this.uid}-speed-front`; }

  ngAfterViewInit(): void {
    const orbi = createOrbi({
      type: this.type,
      id: `${this.uid}-orbi`,
      gaze: this.gaze,
      gazeJitter: this.gazeJitter ?? Math.random(),
      // Jeder Orbi blinzelt und lacht in seinem eigenen Takt: der eine blinzelt
      // gleich, der andere lacht schon, der dritte schaut nur.
      blinkPhase: Math.random(),
      smilePhase: Math.random(),
    });
    this.orbi = orbi;
    const effects = createPersonalityEffects({ type: this.type, id: `${this.uid}-effects` });
    const mount = this.mountRef.nativeElement;
    mount.appendChild(orbi.root);
    mount.after(effects.root);
    const controller = new OrbiController(orbi, sequenceRegistry);
    this.controller = controller;

    controller.addEventListener('sequence-start', (event: Event) => {
      if ((event as CustomEvent).detail?.name !== 'hide-surprise') return;
      this.clipAnimation?.cancel();
      this.clipAnimation = orbi.root.animate(HIDE_SURPRISE_CLIP, {
        duration: sequenceRegistry.get('hide-surprise').meta.duration,
      });
    });

    // Keine Angular-Zustände betroffen — außerhalb der Zone, sonst löst jede
    // Pause, jedes `finished` und jedes Scroll-Event eine Change Detection aus.
    this.zone.runOutsideAngular(() => {
      // Außerhalb des Bildschirms und während gescrollt wird steht der Orbi
      // (Leistung auf dem Dashboard mit vielen Kacheln, Marcel 27.09.2026).
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => this.setPaused('offscreen', !entry.isIntersecting),
        { rootMargin: '120px' },
      );
      this.visibilityObserver.observe(this.host.nativeElement);
      this.unsubscribeScroll = subscribeScroll((isScrolling) => this.setPaused('scroll', isScrolling));

      const { leadMs, steps } = toQueue(this.script);
      this.steps = steps;
      // Bei reduzierter Bewegung kein Ablauf — Play/Stop bleibt bedienbar,
      // der Lab-Controller spielt die Übergänge dann in 1ms.
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.playStop) this.wirePlayStop(orbi.root);
      if (this.playStop && this.active) {
        this.enterInteractive({ activate: true });
        return;
      }
      this.startLoop(leadMs + Math.random() * START_JITTER_MS);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.visibilityObserver?.disconnect();
    this.unsubscribeScroll?.();
    if (this.timer) clearTimeout(this.timer);
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    this.clipAnimation?.cancel();
    this.controller?.stop();
  }

  /** Eine Sequenz über den Lab-Controller, dann ihre Pause, dann die nächste —
   *  endlos. `play()` löst erst nach dem aufgelösten `finished` auf (Lab-README:
   *  "eine Sequenz erst danach durch die nächste ersetzen"). */
  private async runStep(index: number, generation: number): Promise<void> {
    if (this.destroyed || !this.controller || generation !== this.loopGeneration) return;
    if (this.pauseReasons.size) {
      this.pendingStep = () => this.runStep(index, generation);
      return;
    }
    const step = this.steps[index];
    try {
      await this.controller.play(step.name);
    } catch (error) {
      console.warn(`[orbi-lab] Sequenz "${step.name}" fehlgeschlagen`, error);
    }
    if (generation !== this.loopGeneration) return;
    this.wait(step.pauseAfter, () => this.runStep((index + 1) % this.steps.length, generation));
  }

  private startLoop(delayMs: number): void {
    this.stopLoop();
    if (!this.steps.length || this.reducedMotion) return;
    const generation = this.loopGeneration;
    this.wait(delayMs, () => this.runStep(0, generation));
  }

  private stopLoop(): void {
    this.loopGeneration += 1;
    if (this.timer) clearTimeout(this.timer);
    this.pendingStep = null;
  }

  // ── Play / Stop (Lab: core/play-stop-interaction.js) ──────────────────────

  private wirePlayStop(root: HTMLElement): void {
    // Die Lab-Interaktion setzt Rolle/Tabindex erst, wenn sie läuft — damit
    // der Orbi auch im Ruhezustand per Tastatur erreichbar ist, hier dauerhaft.
    root.setAttribute('tabindex', '0');
    root.setAttribute('role', 'button');
    root.addEventListener('pointerenter', () => this.onEnter());
    root.addEventListener('focus', () => this.onEnter());
    root.addEventListener('pointerleave', () => this.onLeave());
    root.addEventListener('blur', () => this.onLeave());
    // Nach Stop zeigt Orbi wieder die Augen, auch wenn der Zeiger noch auf ihm
    // liegt (Marcel, 28.09.2026) — Play erscheint erst beim nächsten Hover.
    // Klickt man in diesem Zustand trotzdem, startet es direkt; ebenso
    // Enter/Leertaste bei Tastatur-Fokus.
    root.addEventListener('click', () => {
      if (this.mode === 'loop') this.enterInteractive({ activate: true });
    });
    root.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.mode !== 'loop' || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      this.enterInteractive({ activate: true });
    });
    root.addEventListener('orbi-play-request', () => this.zone.run(() => this.playRequest.emit()));
    root.addEventListener('orbi-stop-request', () => this.zone.run(() => this.stopRequest.emit()));
  }

  private get playStopState(): PlayStopState | null {
    return (this.orbi?.root.dataset['playStopState'] as PlayStopState | undefined) ?? null;
  }

  private onEnter(): void {
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    if (this.mode === 'loop') this.enterInteractive({ hover: true });
  }

  /** Verlässt der Zeiger den Play-Zustand (nicht Stop), wartet die Rückkehr-
   *  Animation ab und gibt dann an `script` zurück. */
  private onLeave(): void {
    if (this.mode !== 'interactive') return;
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      const root = this.orbi?.root;
      const stillThere = !!root && (root.matches(':hover') || root === document.activeElement);
      if (!stillThere && this.playStopState === 'idle') this.controller?.stop();
    }, PLAY_LEAVE_SETTLE_MS);
  }

  /** Schleife anhalten, Lab-Sequenz `play-button` starten. Ihre Listener
   *  hängen nach `play()` sofort am Orbi; das Ereignis, das hierher geführt
   *  hat, ist aber schon vorbei — daher wird es für sie nachgereicht. */
  private enterInteractive({ hover = false, activate = false }: { hover?: boolean; activate?: boolean }): void {
    if (!this.controller || !this.orbi || this.mode === 'interactive') return;
    this.mode = 'interactive';
    this.stopLoop();
    const done = this.controller.play('play-button');
    const root = this.orbi.root;
    if (hover) root.dispatchEvent(new PointerEvent('pointerenter'));
    if (activate) this.pressPlayStop(root);
    done
      .catch((error: unknown) => console.warn('[orbi-lab] Play/Stop abgebrochen', error))
      .finally(() => this.afterInteraction());
  }

  /** Play-Sequenz beendet (Stop zurückgesetzt oder Zeiger weg): weiter mit
   *  `script`. Bewusst KEIN sofortiges Play, wenn der Zeiger noch auf Orbi
   *  liegt — nach Stop gehören die Augen zurück, nicht das Play-Dreieck. */
  private afterInteraction(): void {
    this.mode = 'loop';
    if (this.destroyed) return;
    this.startLoop(IDLE_MS);
  }

  /** `active` hat sich von außen geändert (Chat im Widget geschlossen oder
   *  anderswo gestartet): Orbi über seinen eigenen Klick nachziehen, damit die
   *  Lab-Übergänge laufen statt zu springen. Der daraus folgende Request ist
   *  für den Aufrufer dann ein No-op. */
  private syncWithActive(): void {
    const state = this.playStopState;
    const showsStop = state === 'stop' || state === 'stop-hover';
    const root = this.orbi?.root;
    if (!root) return;
    if (this.active && !showsStop) {
      if (this.mode === 'loop') this.enterInteractive({ activate: true });
      else this.pressPlayStop(root);
    } else if (!this.active && showsStop) {
      this.pressPlayStop(root);
    }
  }

  /** Löst Play bzw. Stop der Lab-Interaktion aus — über Enter statt `click()`:
   *  läuft gerade ein Klick auf dem Orbi (Klick im Ruhezustand → Play), ist
   *  ein weiteres `root.click()` laut HTML-Spec ein No-op, der Chat startete
   *  sonst erst beim zweiten Klick. Die Lab-Interaktion behandelt Enter wie
   *  einen Klick (`onKeyDown` → `onActivate`). */
  private pressPlayStop(root: HTMLElement): void {
    root.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
  }

  private wait(ms: number, next: () => void): void {
    if (this.destroyed) return;
    this.timer = setTimeout(next, ms);
  }

  /** Hält Lab-CSS (Klasse) und laufende Sequenz (Web Animations) an bzw.
   *  setzt beides dort fort, wo es stand. */
  private setPaused(reason: 'offscreen' | 'scroll', on: boolean): void {
    const wasPaused = this.pauseReasons.size > 0;
    if (on) this.pauseReasons.add(reason);
    else this.pauseReasons.delete(reason);
    const paused = this.pauseReasons.size > 0;
    if (paused === wasPaused) return;

    const hostEl = this.host.nativeElement;
    hostEl.classList.toggle('orbi-lab--paused', paused);
    if (paused) {
      this.pausedAnimations = hostEl
        .getAnimations({ subtree: true })
        .filter((a) => !(a instanceof CSSAnimation) && a.playState === 'running');
      this.pausedAnimations.forEach((a) => a.pause());
      return;
    }
    this.pausedAnimations.forEach((a) => {
      if (a.playState === 'paused') a.play();
    });
    this.pausedAnimations = [];
    const next = this.pendingStep;
    this.pendingStep = null;
    next?.();
  }
}
